import { useMemo } from 'react';

import NsStandout from '@britishgasenergy/react-nucleus/dist/nuc-components/NsStandout';
import { useSelector, shallowEqual } from 'react-redux';

import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { NsList } from 'components/common/ns-list/NsList';
import { useEnergyUsage } from 'hooks/useEnergyUsage';
import { formatEnergy } from 'utils/numberFormat';
import { CONSUMPTION_TYPES, FUEL_TYPES } from 'config/app-config';

type ConsumptionTextProps = {
  consumptionType?: typeof CONSUMPTION_TYPES[keyof typeof CONSUMPTION_TYPES];
  isMoveInJourney?: boolean;
};

const formatEnergyOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
};

export const consumptionTextTestId = 'consumptionTextTestId';

export const ConsumptionText = ({
  consumptionType,
  isMoveInJourney
}: ConsumptionTextProps) => {
  const getEnergyUsage = useEnergyUsage();
  const selectedFuelType = useSelector(selectFuelType, shallowEqual);

  const isLow = consumptionType === CONSUMPTION_TYPES.LOW;
  const isMedium = consumptionType === CONSUMPTION_TYPES.MEDIUM;
  const isHigh = consumptionType === CONSUMPTION_TYPES.HIGH;
  const energyUsage = getEnergyUsage(consumptionType, selectedFuelType);
  const energyUsageHtml = useMemo(
    () => [
      ...(selectedFuelType[FUEL_TYPES.ELECTRIC]
        ? [
            <p key={2}>
              <strong>Electricity: </strong>
              {formatEnergy(
                energyUsage[FUEL_TYPES.ELECTRIC],
                'kwh',
                formatEnergyOptions
              )}
            </p>
          ]
        : []),
      ...(selectedFuelType[FUEL_TYPES.GAS]
        ? [
            <p key={3}>
              <strong>Gas: </strong>
              {formatEnergy(
                energyUsage[FUEL_TYPES.GAS],
                'kwh',
                formatEnergyOptions
              )}
            </p>
          ]
        : [])
    ],
    [energyUsage, selectedFuelType]
  );

  return (
    <div data-testid={consumptionTextTestId}>
      {isLow && (
        <NsList>
          {[
            <p key={0}>Typically a flat or small house</p>,
            <p key={1}>1 or 2 people living there</p>,
            ...energyUsageHtml
          ]}
        </NsList>
      )}
      {isMedium && (
        <NsList>
          {[
            <p key={0}>Typically an average 2-3 bedroom home</p>,
            <p key={1}>2 or 3 people living there</p>,
            ...energyUsageHtml
          ]}
        </NsList>
      )}
      {isHigh && (
        <NsList>
          {[
            <p key={0}>Typically a 4+ bedroom home</p>,
            <p key={1}>4 or more people living there</p>,
            ...energyUsageHtml
          ]}
        </NsList>
      )}

      <NsStandout>
        <h5>Good to know</h5>
        {isMoveInJourney ? (
          <p>
            We can estimate your usage using Ofgem&apos;s national average
            figures. Once you&apos;re settled in, we&apos;ll have a better idea
            of how much energy you&apos;re actually using based on your meter
            readings.
          </p>
        ) : (
          <p>
            We use Ofgem&apos;s national average to estimate your energy use,
            but for a more accurate quote, use the figures from your energy
            bill. You&apos;ll find them on your supplier&apos;s app, online
            account or paper bill. No bill? We&apos;ll review our estimate in a
            few weeks to make sure it&apos;s right.
          </p>
        )}
      </NsStandout>
    </div>
  );
};
